import { FileText, MapPin, PhoneCall, Route as RouteIcon, TicketCheck, UserPlus } from 'lucide-react';

import { DonutChart, type DonutSlice } from '@/components/ui/DonutChart';
import { ProgressBar } from '@/components/ui/ProgressBar';
import type { ClassDistribution, StudentTransportSummary } from '@/types/transport';
import { QuickActionGrid, RailCard, type QuickAction } from './TransportSideRail';

export interface StudentsSideRailProps {
  summary: StudentTransportSummary;
  distribution: ClassDistribution[];
  actions: QuickAction[];
  onViewStudentsReport?: () => void;
  onViewClassReport?: () => void;
}

/**
 * The Students tab's rail: how many ride, and which classes they come from.
 *
 * The class breakdown is bars rather than a second ring. Six slices of one
 * circle is a chart nobody can read a value off; six bars against a shared
 * scale answers "which class fills the buses" at a glance, which is the
 * question a transport office actually asks before it plans a route.
 */
export function StudentsSideRail({
  summary,
  distribution,
  actions,
  onViewStudentsReport,
  onViewClassReport,
}: StudentsSideRailProps) {
  const slices: DonutSlice[] = [
    {
      key: 'using',
      label: 'Using transport',
      value: summary.usingTransport,
      stroke: 'stroke-emerald-500',
      dot: 'bg-emerald-500',
    },
    {
      key: 'notUsing',
      label: 'Not using',
      value: summary.notUsingTransport,
      stroke: 'stroke-violet-500',
      dot: 'bg-violet-500',
    },
  ];

  // Every bar is drawn against the biggest class rather than against the roll,
  // so the shortest bar is still visible — a class of 17 against a total of 245
  // would otherwise be a line too thin to see.
  const busiest = Math.max(...distribution.map((row) => row.students), 1);

  return (
    <div className="flex flex-col gap-4">
      <RailCard
        title="Students overview"
        action={onViewStudentsReport && { label: 'View report', onSelect: onViewStudentsReport }}
      >
        <DonutChart
          slices={slices}
          total={summary.totalStudents}
          caption="Total students"
          layout="responsive"
        />
      </RailCard>

      <RailCard
        title="Class-wise distribution"
        action={onViewClassReport && { label: 'View report', onSelect: onViewClassReport }}
      >
        <ul className="flex flex-col gap-3">
          {distribution.map((row, index) => (
            <li
              key={row.label}
              className="animate-fade-up"
              style={{ animationDelay: `${Math.min(index, 6) * 50}ms` }}
            >
              <div className="flex items-baseline justify-between gap-3">
                <span className="truncate text-sm font-semibold text-ink-2">{row.label}</span>
                <span className="shrink-0 text-sm font-bold text-ink">{row.students}</span>
              </div>
              <ProgressBar
                value={(row.students / busiest) * 100}
                label={`${row.label}: ${row.students} students on transport`}
                hideLabel
                size="sm"
                className="mt-1.5"
              />
            </li>
          ))}
        </ul>
      </RailCard>

      <RailCard title="Quick actions">
        <QuickActionGrid actions={actions} />
      </RailCard>
    </div>
  );
}

/** The six things the office does to a child on transport. */
export const STUDENT_ACTION_ICONS = {
  addStudent: UserPlus,
  assignRoute: RouteIcon,
  manageStops: MapPin,
  studentReport: FileText,
  transportPass: TicketCheck,
  parentContact: PhoneCall,
};
